import type { MessageAttachmentDto } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { MessageAudioFile } from "../MessageAudioFile/index.js";
import { MessageDocument } from "../MessageDocument/index.js";
import { MessageMediaImage, type InboxImageLayout } from "../MessageMediaImage/MessageMediaImage.js";
import { MessageMediaImageSkeleton } from "../MessageMediaImage/MessageMediaImageSkeleton.js";
import { MessageSticker } from "../MessageSticker/index.js";
import { MessageVideo } from "../MessageVideo/index.js";
import { MessageVoice } from "../MessageVoice/index.js";
import {
  isAudioAttachment,
  isDocumentAttachment,
  isImageAttachment,
  isVideoAttachment,
  isVoiceAttachment,
  usesStickerPresentation,
} from "../attachment-kind.js";
import { shouldRenderProviderAttachmentUrl } from "../attachment-media-display.js";
import { INBOX_ATTACHMENT_IMAGE_SHELL_CLASS } from "../inbox-image-frame.js";
import { useStoredAttachmentUrl } from "../use-stored-attachment-url.js";

function AttachmentStatusLabel({
  attachment,
  framed = false,
  layout = "default",
}: {
  attachment: MessageAttachmentDto;
  framed?: boolean;
  layout?: InboxImageLayout;
}): React.JSX.Element {
  const { t } = useTranslation();
  const state = attachment.storageState;

  let message: string;
  if (state === "PENDING_STORAGE") {
    message = t("inbox.media.processing");
  } else if (state === "EXPIRED") {
    message = t("inbox.media.expired");
  } else {
    message = t("inbox.media.unavailable");
  }

  if (framed) {
    const isBubble = layout === "bubble";
    return (
      <div
        className={cn(
          INBOX_ATTACHMENT_IMAGE_SHELL_CLASS,
          isBubble ? "rounded-[inherit]" : "rounded-md",
          !isBubble && "my-1",
          "flex items-center justify-center px-3 text-center",
        )}
      >
        <p className="m-0 text-[0.8125rem] text-ink-subtle">{message}</p>
      </div>
    );
  }

  return <p className="m-0 text-[0.8125rem] text-ink-subtle">{message}</p>;
}

function StoredImageBody({
  organizationId,
  attachment,
  layout,
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
  layout: InboxImageLayout;
}): React.JSX.Element {
  const { t } = useTranslation();
  const urlState = useStoredAttachmentUrl(organizationId, attachment);

  if (urlState.status === "error") {
    return <AttachmentStatusLabel attachment={attachment} framed layout={layout} />;
  }

  const urlReady = urlState.status === "ready";
  const url = urlReady ? urlState.url : undefined;
  const urlResolving = urlState.status === "loading" || urlState.status === "idle";

  return (
    <MessageMediaImage
      url={url}
      urlResolving={urlResolving}
      layout={layout}
      alt={t("inbox.messageType.IMAGE")}
      fileName={attachment.fileName}
      mimeType={attachment.mimeType}
      organizationId={organizationId}
      attachmentId={attachment.attachmentId}
      onRefreshUrl={urlState.refreshUrl}
      ensureUrl={urlState.ensureUrl}
    />
  );
}

/**
 * Thin attachment router. IMAGE keeps the existing compact photo path.
 * Other types render dedicated components.
 */
export function MessageAttachmentMedia({
  organizationId,
  attachment,
  locale: _locale,
  layout = "default",
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
  locale: string;
  layout?: InboxImageLayout;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (usesStickerPresentation(attachment)) {
    return <MessageSticker organizationId={organizationId} attachment={attachment} />;
  }

  if (isImageAttachment(attachment)) {
    if (shouldRenderProviderAttachmentUrl(attachment)) {
      return (
        <MessageMediaImage
          url={attachment.url!}
          layout={layout}
          alt={t("inbox.messageType.IMAGE")}
          fileName={attachment.fileName}
          mimeType={attachment.mimeType}
        />
      );
    }

    if (attachment.storageState === "STORED") {
      return (
        <StoredImageBody
          organizationId={organizationId}
          attachment={attachment}
          layout={layout}
        />
      );
    }

    if (attachment.storageState === "PENDING_STORAGE") {
      return (
        <MessageMediaImageSkeleton layout={layout} label={t("inbox.media.processing")} />
      );
    }

    return (
      <AttachmentStatusLabel attachment={attachment} framed layout={layout} />
    );
  }

  if (isVideoAttachment(attachment)) {
    return (
      <MessageVideo
        organizationId={organizationId}
        attachment={attachment}
        layout={layout}
      />
    );
  }

  if (isVoiceAttachment(attachment)) {
    return <MessageVoice organizationId={organizationId} attachment={attachment} />;
  }

  if (isAudioAttachment(attachment)) {
    return <MessageAudioFile organizationId={organizationId} attachment={attachment} />;
  }

  if (isDocumentAttachment(attachment)) {
    return <MessageDocument organizationId={organizationId} attachment={attachment} />;
  }

  return <AttachmentStatusLabel attachment={attachment} />;
}
